/**
 * Shared highlight color definitions used by both client and server
 *
 * These colors are used in the TipTap editor for highlighting text
 * and exported to DOCX using custom shading colors.
 */

export interface HighlightColorDefinition {
  light: string;
  dark: string;
  name: string;
}

/**
 * Available highlight colors in the app
 * Each color has a light mode and dark mode variant
 *
 * The light mode color is always used for DOCX export to ensure
 * consistent appearance across all exported documents.
 */
export const HIGHLIGHT_COLORS: HighlightColorDefinition[] = [
  { light: "#FFF3BF", dark: "#5A4B1A", name: "yellow" },
  { light: "#E2F6DA", dark: "#1F3D2B", name: "green" },
  { light: "#E3EEFF", dark: "#1E2F4D", name: "blue" },
  { light: "#FFE5D6", dark: "#4A2E24", name: "peach" },
  { light: "#DDF5F1", dark: "#1F3A3A", name: "teal" },
  { light: "#FFE1EC", dark: "#4A2434", name: "pink" },
];

export const DEFAULT_HIGHLIGHT_COLOR: HighlightColorDefinition = HIGHLIGHT_COLORS[0];

/** Returns the light-mode hex if it matches the manuscript highlighter palette; otherwise undefined. */
export function normalizePreferredManuscriptHighlightLight(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  return HIGHLIGHT_COLORS.some((c) => c.light === raw) ? raw : undefined;
}

/**
 * Resolve a color name (e.g. "green") or a light-mode hex string to a palette light hex.
 * Names are matched case-insensitively. Returns undefined when no palette entry matches.
 */
export function resolveHighlightColorLight(nameOrHex: string): string | undefined {
  const lower = nameOrHex.trim().toLowerCase();
  const byName = HIGHLIGHT_COLORS.find((c) => c.name.toLowerCase() === lower);
  if (byName) return byName.light;
  return normalizePreferredManuscriptHighlightLight(nameOrHex);
}
